import React from "react";
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";
import ValuationCards from '../Valuations/ValuationCards';
import { ValuationCard, Valuation, ValuationHeaderData } from '../../models/Valuation';
import './ValuationCards.css';
import { useCurrency } from '../../components/CurrencyContext';
import PaidIcon from '@mui/icons-material/Paid';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';



const ValuationHeader: React.FC<ValuationHeaderData> = ({
    artworkId,
    acquisition,
    insurance,
    fairMarketValue,
    exchangeRates
}) => {
    const { currency } = useCurrency();

    const renderCurrentBaseValue = (v: Valuation) => {

        if (exchangeRates) {
            const fromRate = exchangeRates[v.currencyCode] || 1;
            const toRate = exchangeRates[currency] || 1;
            const convertedLowValue = (v.lowerRangeAmount / fromRate) * toRate;
            const convertedHighValue = (v.upperRangeAmount / fromRate) * toRate;

            if (v.valuationTypeId === 4) {
                return `≈ ${Math.round(convertedLowValue).toLocaleString()}–${Math.round(convertedHighValue).toLocaleString()} ${currency}`;
            } else {
                return `≈ ${Math.round(convertedLowValue).toLocaleString()} ${currency}`;
            }
        }
    };

    const formatDate = (v?: Valuation | null) => {
        if (!v) return "No valuation recorded";
        const d = new Date(v.valuationDate);
        if (isNaN(d.getTime())) return "Date unavailable";
        return `Last updated: ${d.toLocaleDateString()}`;
    };

    const formatValue = (v?: Valuation | null) => {
        if (!v) return "Not set";

        const valuationText =
            v.valuationTypeId === 4
                ? `${v.lowerRangeAmount?.toLocaleString(undefined, { maximumFractionDigits: 0, })}–${v.upperRangeAmount?.toLocaleString(undefined, { maximumFractionDigits: 0, })}`
                : `${v.lowerRangeAmount?.toLocaleString(undefined, { maximumFractionDigits: 0, })} ${v.currencyCode}`;
        
        return `${valuationText}`;
    };

    const formatBaseCurrenyValue = (v?: Valuation | null) => {
        if (!v) return "";

        const baseCurrencyText = renderCurrentBaseValue(v);

        return `${baseCurrencyText}`;
    };

    const renderCard = (
        label: string,
        data: Valuation | null | undefined,
        IconComponent: typeof PaidIcon,
        modifierClass: string
    ) => (
        <div className={`valuation-card ${modifierClass}`}>
            <div className="valuation-card-header">
                <div className="valuation-card-icon">
                    <IconComponent fontSize="small" />
                </div>
                <div className="valuation-card-title">{label}</div>
            </div>

            <div className="valuation-card-value">
                {formatValue(data)}
            </div>

            <div className="valuation-card-meta">
                {formatBaseCurrenyValue(data)}
            </div>
            <div className="valuation-card-meta">
                {formatDate(data)}
            </div>
        </div>
    );

    return (
        <div className="valuation-section">
            <div className="section-header">
                <h3>Key Item Values</h3>
            </div>
            <div className="valuation-cards">
                {renderCard("Acquisition Value", acquisition, PaidIcon, "valuation-card--acquisition")}
                {renderCard("Insurance Value", insurance, ShieldIcon, "valuation-card--insurance")}
                {renderCard("Market Value", fairMarketValue, TrendingUpIcon, "valuation-card--fmv")}
            </div>
        </div>
    );
};

export default ValuationHeader;
